import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
	Users,
	UserPlus,
	UserMinus,
	Mail,
	Phone,
	Shield,
	Crown,
	Briefcase,
	Search,
	MoreVertical,
	Edit2,
	Trash2,
	Check,
	X,
	ChevronDown,
	ChevronRight,
} from 'lucide-react'
import { Button } from '@components/common'

/**
 * TeamTab Component
 *
 * Displays project team members organized by roles
 * with user-friendly management interface
 */
const TeamTab = ({ members, canManage, projectId, onManageClick, onRemoveMember }) => {
	const [searchQuery, setSearchQuery] = useState('')
	const [expandedRoles, setExpandedRoles] = useState({})
	const [viewMode, setViewMode] = useState('role') // 'role' or 'list'

	// Get initials from name
	const getInitials = (name) => {
		if (!name) return '?'
		const parts = name.split(' ')
		if (parts.length >= 2) {
			return (parts[0][0] + parts[1][0]).toUpperCase()
		}
		return name.substring(0, 2).toUpperCase()
	}

	// Get full name
	const getFullName = (member) => {
		if (member.first_name && member.last_name) {
			return `${member.first_name} ${member.last_name}`
		}
		return member.name || member.username || member.email || 'Unknown User'
	}

	// Get role display
	const getRoleDisplay = (member) => {
		if (member.role_name) return member.role_name
		if (member.role?.name) return member.role.name
		return member.role || 'Team Member'
	}

	// Get role icon
	const getRoleIcon = (roleSlug) => {
		switch (roleSlug?.toLowerCase()) {
			case 'super-admin':
			case 'superadmin':
				return Crown
			case 'company-admin':
			case 'admin':
				return Shield
			case 'compliance-manager':
			case 'project-manager':
				return Briefcase
			default:
				return Users
		}
	}

	// Get avatar color based on name
	const getAvatarColor = (name) => {
		const colors = [
			'#3B82F6', // blue
			'#10B981', // green
			'#F59E0B', // amber
			'#EF4444', // red
			'#8B5CF6', // purple
			'#EC4899', // pink
			'#06B6D4', // cyan
			'#F97316', // orange
		]
		const index = (name || '').charCodeAt(0) % colors.length
		return colors[index]
	}

	// Filter members
	const filteredMembers = useMemo(() => {
		if (!searchQuery.trim()) return members

		const query = searchQuery.toLowerCase()
		return members.filter((member) => {
			const name = getFullName(member).toLowerCase()
			const email = (member.email || '').toLowerCase()
			const role = getRoleDisplay(member).toLowerCase()
			return name.includes(query) || email.includes(query) || role.includes(query)
		})
	}, [members, searchQuery])

	// Group members by role
	const membersByRole = useMemo(() => {
		const groups = {}

		filteredMembers.forEach((member) => {
			const roleKey = member.role_name || member.role?.name || 'Team Member'
			if (!groups[roleKey]) {
				groups[roleKey] = {
					name: roleKey,
					slug: member.role_slug || member.role?.slug || 'team-member',
					members: [],
				}
			}
			groups[roleKey].members.push(member)
		})

		// Sort roles by priority
		const rolePriority = {
			'Super Admin': 1,
			'Company Admin': 2,
			'Compliance Manager': 3,
			'Project Manager': 4,
		}

		return Object.values(groups).sort((a, b) => {
			const priorityA = rolePriority[a.name] || 99
			const priorityB = rolePriority[b.name] || 99
			return priorityA - priorityB
		})
	}, [filteredMembers])

	// Toggle role expansion
	const toggleRole = (roleName) => {
		setExpandedRoles((prev) => ({
			...prev,
			[roleName]: prev[roleName] === false ? true : false,
		}))
	}

	// Render member card
	const renderMemberCard = (member, showRole = false) => {
		const name = getFullName(member)
		const initials = getInitials(name)
		const role = getRoleDisplay(member)
		const avatarColor = getAvatarColor(name)
		const isActive = member.status === 'active'

		return (
			<div key={member.id} className="team-member-card">
				<div
					className="team-member-card__avatar"
					style={{ backgroundColor: avatarColor }}
				>
					{member.avatar_url ? (
						<img src={member.avatar_url} alt={name} />
					) : (
						initials
					)}
					<span
						className={`team-member-card__status ${isActive ? 'team-member-card__status--active' : ''}`}
					/>
				</div>

				<div className="team-member-card__info">
					<h4 className="team-member-card__name">{name}</h4>
					{showRole && (
						<span className="team-member-card__role">{role}</span>
					)}
					{member.email && (
						<span className="team-member-card__email">
							<Mail size={12} />
							{member.email}
						</span>
					)}
				</div>

				<div className="team-member-card__actions">
					{member.email && (
						<a
							href={`mailto:${member.email}`}
							className="team-member-card__action-btn"
							title="Send email"
						>
							<Mail size={14} />
						</a>
					)}
					{canManage && (
						<button
							className="team-member-card__action-btn team-member-card__action-btn--danger"
							onClick={() => onRemoveMember?.(member.id)}
							title="Remove from project"
						>
							<UserMinus size={14} />
						</button>
					)}
				</div>
			</div>
		)
	}

	return (
		<div className="team-tab">
			{/* Toolbar */}
			<div className="team-tab__toolbar">
				<div className="team-tab__search">
					<Search size={16} />
					<input
						type="text"
						placeholder="Search team members..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</div>

				<div className="team-tab__actions">
					<div className="view-toggle">
						<button
							className={`view-toggle__btn ${viewMode === 'role' ? 'view-toggle__btn--active' : ''}`}
							onClick={() => setViewMode('role')}
							title="Group by role"
						>
							<Shield size={14} />
						</button>
						<button
							className={`view-toggle__btn ${viewMode === 'list' ? 'view-toggle__btn--active' : ''}`}
							onClick={() => setViewMode('list')}
							title="List view"
						>
							<Users size={14} />
						</button>
					</div>

					{canManage && onManageClick && (
						<Button variant="primary" size="small" onClick={onManageClick}>
							<UserPlus size={14} />
							Manage Team
						</Button>
					)}
				</div>
			</div>

			{/* Summary */}
			<div className="team-tab__summary">
				<div className="team-summary-card">
					<Users size={24} />
					<div className="team-summary-card__content">
						<span className="team-summary-card__value">{members.length}</span>
						<span className="team-summary-card__label">Team Members</span>
					</div>
				</div>
				<div className="team-summary-card">
					<Shield size={24} />
					<div className="team-summary-card__content">
						<span className="team-summary-card__value">{membersByRole.length}</span>
						<span className="team-summary-card__label">Roles</span>
					</div>
				</div>
			</div>

			{/* Team Content */}
			{filteredMembers.length > 0 ? (
				viewMode === 'role' ? (
					// Role-based view
					<div className="team-tab__roles">
						{membersByRole.map((roleGroup) => {
							const isExpanded = expandedRoles[roleGroup.name] !== false
							const RoleIcon = getRoleIcon(roleGroup.slug)

							return (
								<div key={roleGroup.name} className="role-group">
									<button
										className="role-group__header"
										onClick={() => toggleRole(roleGroup.name)}
									>
										<div className="role-group__left">
											<span className="role-group__chevron">
												{isExpanded ? (
													<ChevronDown size={16} />
												) : (
													<ChevronRight size={16} />
												)}
											</span>
											<div className="role-group__icon">
												<RoleIcon size={18} />
											</div>
											<span className="role-group__name">{roleGroup.name}</span>
										</div>
										<span className="role-group__count">
											{roleGroup.members.length}{' '}
											{roleGroup.members.length === 1 ? 'member' : 'members'}
										</span>
									</button>

									{isExpanded && (
										<div className="role-group__members">
											{roleGroup.members.map((member) =>
												renderMemberCard(member, false)
											)}
										</div>
									)}
								</div>
							)
						})}
					</div>
				) : (
					// List view
					<div className="team-tab__list">
						{filteredMembers.map((member) => renderMemberCard(member, true))}
					</div>
				)
			) : (
				<div className="team-tab__empty">
					<Users size={48} />
					<h4>
						{searchQuery ? 'No Members Found' : 'No Team Members'}
					</h4>
					<p>
						{searchQuery
							? 'No team members match your search criteria.'
							: 'No team members have been assigned to this project yet.'}
					</p>
					{!searchQuery && canManage && onManageClick && (
						<Button variant="primary" size="small" onClick={onManageClick}>
							<UserPlus size={14} />
							Add Team Members
						</Button>
					)}
					{searchQuery && (
						<Button
							variant="outline"
							size="small"
							onClick={() => setSearchQuery('')}
						>
							Clear Search
						</Button>
					)}
				</div>
			)}
		</div>
	)
}

TeamTab.propTypes = {
	members: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			first_name: PropTypes.string,
			last_name: PropTypes.string,
			name: PropTypes.string,
			username: PropTypes.string,
			email: PropTypes.string,
			role: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
			role_name: PropTypes.string,
			role_slug: PropTypes.string,
			status: PropTypes.string,
			avatar_url: PropTypes.string,
		})
	).isRequired,
	canManage: PropTypes.bool,
	projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	onManageClick: PropTypes.func,
	onRemoveMember: PropTypes.func,
}

TeamTab.defaultProps = {
	canManage: false,
	onManageClick: null,
	onRemoveMember: null,
}

export default TeamTab
